import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { format } from 'date-fns';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, Eye,
  Calendar as CalendarIcon, RefreshCw, AlertCircle, Package,
  ArrowUpRight, ArrowDownRight, BarChart3, PieChart, Activity,
  Star, Clock, Target, Lightbulb
} from 'lucide-react';

// Date range presets
const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'last_3_days', label: 'Last 3 Days' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_15_days', label: 'Last 15 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'custom', label: 'Custom Range' }
];

// Analytics Stat Card
const AnalyticsStatCard = ({ title, value, icon: Icon, trend, description, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600'
  };

  const getTrendColor = () => {
    if (!trend) return 'text-gray-500';
    return trend.direction === 'up' ? 'text-green-600' : 
           trend.direction === 'down' ? 'text-red-600' : 'text-gray-500';
  };

  const TrendIcon = trend?.direction === 'up' ? ArrowUpRight : 
                    trend?.direction === 'down' ? ArrowDownRight : null;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-3">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        <p className="text-3xl font-bold mb-2">{value}</p>
        {trend && (
          <div className={`flex items-center text-sm ${getTrendColor()}`}>
            {TrendIcon && <TrendIcon className="h-4 w-4 mr-1" />}
            <span>{trend.percentage}% from previous period</span>
          </div>
        )}
        {description && !trend && (
          <p className="text-sm text-gray-500">{description}</p>
        )}
      </CardContent>
    </Card>
  );
};

// Performance Chart Placeholder (for future chart integration)
const PerformanceChart = ({ title, subtitle, data }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-500" />
          {title}
        </CardTitle>
        {subtitle && <CardDescription>{subtitle}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="h-64 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-dashed border-gray-200">
          <div className="text-center">
            <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">Chart visualization</p>
            <p className="text-xs text-gray-400">Data available for {data?.length || 0} items</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Top Performers Table
const TopPerformersTable = ({ title, icon: Icon, items, columns, emptyMessage }) => {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Icon className="h-5 w-5 text-blue-500" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-center py-8">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">{emptyMessage || 'No data available'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-sm font-medium text-gray-500">Rank</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-500">{columns.titleHeader || 'Name'}</th>
                  <th className="text-right py-2 text-sm font-medium text-gray-500">{columns.valueHeader || 'Value'}</th>
                  {columns.secondary && (
                    <th className="text-right py-2 text-sm font-medium text-gray-500">{columns.secondaryHeader}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={item.id || item.name || `${item.title || "item"}-${idx}`} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-3">
                      <Badge variant={idx < 3 ? 'default' : 'secondary'}>
                        #{idx + 1}
                      </Badge>
                    </td>
                    <td className="py-3">
                      <p className="font-medium text-gray-900">{item[columns.title]}</p>
                      {columns.subtitle && (
                        <p className="text-xs text-gray-500">{item[columns.subtitle]}</p>
                      )}
                    </td>
                    <td className="py-3 text-right font-semibold">
                      {columns.valuePrefix}{item[columns.value]?.toLocaleString()}{columns.valueSuffix}
                    </td>
                    {columns.secondary && (
                      <td className="py-3 text-right text-gray-600">
                        {item[columns.secondary]?.toLocaleString()} {columns.secondaryLabel}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Insights Card
const InsightsCard = ({ suggestions }) => {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-red-500 bg-red-50';
      case 'medium': return 'border-l-yellow-500 bg-yellow-50';
      default: return 'border-l-blue-500 bg-blue-50';
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-yellow-500" />
          Performance Insights
        </CardTitle>
        <CardDescription>AI-powered recommendations based on your data</CardDescription>
      </CardHeader>
      <CardContent>
        {suggestions.length === 0 ? (
          <div className="text-center py-8">
            <Target className="h-12 w-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">No insights available at this time</p>
          </div>
        ) : (
          <div className="space-y-3">
            {suggestions.map((suggestion, idx) => (
              <div
                key={suggestion.id || suggestion.name || `${suggestion.title || "suggestion"}-${idx}`}
                className={`p-4 rounded-lg border-l-4 ${getPriorityColor(suggestion.priority)}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{suggestion.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{suggestion.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline">{suggestion.category}</Badge>
                      {suggestion.metric_trigger && (
                        <span className="text-xs text-gray-500">{suggestion.metric_trigger}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Analytics Component
const Analytics = () => {
  const { isAdmin, user, apiCall } = useAuth();
  const [datePreset, setDatePreset] = useState('last_30_days');
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchAnalyticsData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = isAdmin() 
        ? '/api/dashboard/admin/stats' 
        : '/api/dashboard/vendor/stats';

      let params = `?preset=${datePreset}`;
      if (datePreset === 'custom' && customDateRange.from && customDateRange.to) {
        params = `?preset=custom&start_date=${customDateRange.from.toISOString()}&end_date=${customDateRange.to.toISOString()}`;
      }

      const [statsResponse, suggestionsResponse] = await Promise.all([
        apiCall(`${endpoint}${params}`),
        apiCall(`/api/dashboard/suggestions?role=${isAdmin() ? 'admin' : 'vendor'}`)
      ]);

      setAnalyticsData(statsResponse);
      setSuggestions(suggestionsResponse.suggestions || []);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, datePreset, customDateRange]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const handleDatePresetChange = (value) => {
    setDatePreset(value);
    if (value === 'custom') {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
    }
  };

  const handleCustomDateSelect = (range) => {
    setCustomDateRange(range);
    if (range.from && range.to) {
      setShowDatePicker(false);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-10 w-40" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i.id || i.name || `${i.title || "i"}-${i}`} className="h-32" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-80" />
            <Skeleton className="h-80" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={fetchAnalyticsData}>
            <RefreshCw className="h-4 w-4 mr-2" /> Retry
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const overview = analyticsData?.overview || {};
  const dateRange = analyticsData?.date_range || {};

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header with Date Range Selector */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-600 mt-1">
              Track your business performance and insights
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={datePreset} onValueChange={handleDatePresetChange}>
              <SelectTrigger className="w-[180px]">
                <CalendarIcon className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                {DATE_PRESETS.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {datePreset === 'custom' && (
              <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    {customDateRange.from && customDateRange.to
                      ? `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d')}`
                      : 'Pick dates'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="range"
                    selected={customDateRange}
                    onSelect={handleCustomDateSelect}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            )}

            <Button variant="outline" size="icon" onClick={fetchAnalyticsData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Date Range Display */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm text-blue-800">
          Showing data for: <strong>{dateRange.period_days} days</strong> 
          {dateRange.start && dateRange.end && (
            <span className="ml-2">
              ({format(new Date(dateRange.start), 'MMM d, yyyy')} - {format(new Date(dateRange.end), 'MMM d, yyyy')})
            </span>
          )}
        </div>

        {/* Tabs for different views */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <AnalyticsStatCard
                title="Total Revenue"
                value={`₹${(overview.revenue?.total || 0).toLocaleString()}`}
                icon={DollarSign}
                trend={overview.revenue?.trend}
                color="green"
              />
              <AnalyticsStatCard
                title="Total Orders"
                value={overview.orders?.total || 0}
                icon={ShoppingCart}
                trend={overview.orders?.trend}
                color="blue"
              />
              {isAdmin() ? (
                <>
                  <AnalyticsStatCard
                    title="Total Customers"
                    value={overview.customers?.total || 0}
                    icon={Users}
                    trend={overview.customers?.trend}
                    color="purple"
                  />
                  <AnalyticsStatCard
                    title="Active Vendors"
                    value={overview.vendors?.active || 0}
                    icon={Activity}
                    description={`${overview.vendors?.total || 0} total vendors`}
                    color="orange"
                  />
                </>
              ) : (
                <>
                  <AnalyticsStatCard
                    title="Page Views"
                    value={(overview.views?.total || 0).toLocaleString()}
                    icon={Eye}
                    trend={overview.views?.trend}
                    color="purple"
                  />
                  <AnalyticsStatCard
                    title="Average Rating"
                    value={overview.rating?.average || 0}
                    icon={Star}
                    description={`${overview.rating?.count || 0} reviews`}
                    color="orange"
                  />
                </>
              )}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PerformanceChart
                title="Revenue Trend"
                subtitle="Revenue over the selected period"
                data={[]}
              />
              <PerformanceChart
                title="Orders Trend"
                subtitle="Order volume over the selected period"
                data={[]}
              />
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {isAdmin() ? (
                <>
                  <TopPerformersTable
                    title="Top Vendors by Revenue"
                    icon={Users}
                    items={analyticsData?.top_vendors || []}
                    columns={{
                      titleHeader: 'Vendor',
                      title: 'name',
                      subtitle: 'email',
                      valueHeader: 'Revenue',
                      value: 'revenue',
                      valuePrefix: '₹',
                      secondaryHeader: 'Orders',
                      secondary: 'orders'
                    }}
                    emptyMessage="No vendor data for this period"
                  />
                  <TopPerformersTable
                    title="Top Products by Sales"
                    icon={Package}
                    items={analyticsData?.top_products || []}
                    columns={{
                      titleHeader: 'Product',
                      title: 'title',
                      subtitle: 'type',
                      valueHeader: 'Revenue',
                      value: 'revenue',
                      valuePrefix: '₹',
                      secondaryHeader: 'Units',
                      secondary: 'sales'
                    }}
                    emptyMessage="No product data for this period"
                  />
                </>
              ) : (
                <>
                  <TopPerformersTable
                    title="Top Products by Sales"
                    icon={Package}
                    items={analyticsData?.top_products || []}
                    columns={{
                      titleHeader: 'Product',
                      title: 'title',
                      subtitle: 'type',
                      valueHeader: 'Revenue',
                      value: 'revenue',
                      valuePrefix: '₹',
                      secondaryHeader: 'Units',
                      secondary: 'sales'
                    }}
                    emptyMessage="No product data for this period"
                  />
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="h-5 w-5 text-blue-500" />
                        Booking Performance
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-blue-50 rounded-lg text-center">
                          <p className="text-3xl font-bold text-blue-700">
                            {overview.bookings?.total || 0}
                          </p>
                          <p className="text-sm text-gray-600">Total Bookings</p>
                        </div>
                        <div className="p-4 bg-green-50 rounded-lg text-center">
                          <p className="text-3xl font-bold text-green-700">
                            {overview.bookings?.trend?.percentage || 0}%
                          </p>
                          <p className="text-sm text-gray-600">Growth Rate</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {/* Ad Campaigns (Admin only) */}
            {isAdmin() && analyticsData?.ad_campaigns?.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="h-5 w-5 text-purple-500" />
                    Campaign Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 text-sm font-medium text-gray-500">Campaign</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-500">Spent</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-500">Impressions</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-500">Clicks</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-500">Revenue</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-500">ROI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.ad_campaigns.map((campaign, idx) => (
                          <tr key={campaign.id || campaign.name || `${campaign.title || "campaign"}-${idx}`} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="py-3 font-medium">{campaign.name}</td>
                            <td className="py-3 text-right">₹{campaign.spent?.toLocaleString()}</td>
                            <td className="py-3 text-right">{campaign.impressions?.toLocaleString()}</td>
                            <td className="py-3 text-right">{campaign.clicks?.toLocaleString()}</td>
                            <td className="py-3 text-right">₹{campaign.revenue?.toLocaleString()}</td>
                            <td className="py-3 text-right">
                              <Badge className={campaign.roi >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                {campaign.roi >= 0 ? '+' : ''}{campaign.roi}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" className="space-y-6">
            <InsightsCard suggestions={suggestions} />

            {/* Quick Stats Summary */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-blue-500" />
                  Quick Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">
                      {analyticsData?.pending_actions?.length || 0}
                    </p>
                    <p className="text-sm text-gray-600">Pending Actions</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">
                      {isAdmin() ? analyticsData?.top_vendors?.length || 0 : analyticsData?.top_products?.length || 0}
                    </p>
                    <p className="text-sm text-gray-600">{isAdmin() ? 'Active Sellers' : 'Top Products'}</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">
                      {analyticsData?.recent_transactions?.length || analyticsData?.recent_orders?.length || 0}
                    </p>
                    <p className="text-sm text-gray-600">Recent Orders</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">
                      {suggestions.length}
                    </p>
                    <p className="text-sm text-gray-600">Recommendations</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Analytics;
